import React, { useState, useEffect, useRef } from 'react';
import { SparklesIcon, ArrowUpIcon, PaperClipIcon, ChevronDownIcon, ChevronRightIcon, LightBulbIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { useIntl } from 'react-intl';
import { createConsumer } from '@rails/actioncable';
import MarkdownMessage from '../../../assistant/components/MarkdownMessage.jsx';
import AgentInfoWidgets from './AgentInfoWidgets';
import { parseAgentMessage } from './utils/parseAgentMessage';
import CopyMessageButton from './CopyMessageButton';

/**
 * InsightMessage - Component for displaying agent insights in a collapsible format
 */
const InsightMessage = ({ insight, onExpand }) => {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);
  
  const handleToggle = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded && onExpand) {
      onExpand(insight);
    }
  };
  
  return (
    <div style={{
      backgroundColor: '#374151',
      borderRadius: '18px',
      padding: '16px 20px',
      border: '1px solid #4b5563',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    }}
    onClick={handleToggle}
    onMouseEnter={(e) => {
      e.currentTarget.style.borderColor = '#3b82f6';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = '#4b5563';
    }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <LightBulbIcon style={{ 
          width: '20px', 
          height: '20px', 
          color: '#fbbf24',
          flexShrink: 0
        }} />
        <div style={{ flex: 1 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px'
          }}>
            <span style={{
              color: '#f9fafb',
              fontWeight: '600',
              fontSize: '15px'
            }}>
              {intl.formatMessage({ id: 'succession.agent_system.ai_assistant_chat.new_insight' })} {insight.title || insight.summary}
            </span>
            {isExpanded ? (
              <ChevronDownIcon style={{ width: '16px', height: '16px', color: '#9ca3af' }} />
            ) : (
              <ChevronRightIcon style={{ width: '16px', height: '16px', color: '#9ca3af' }} />
            )}
          </div>
          
          {isExpanded && (
            <div style={{
              marginTop: '12px',
              color: '#e5e7eb',
              fontSize: '14px',
              lineHeight: 1.6
            }}>
              {insight.body || insight.details}
              
              {insight.actions && insight.actions.length > 0 && (
                <div style={{
                  marginTop: '12px',
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap'
                }}>
                  {insight.actions.map((action, idx) => (
                    <button
                      key={idx}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (action.url) {
                          window.location.href = action.url;
                        }
                      }}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: action.primary ? '#3b82f6' : '#4b5563',
                        border: 'none',
                        borderRadius: '12px',
                        color: 'white',
                        fontSize: '13px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.opacity = '0.8';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.opacity = '1';
                      }}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * AIAssistantChat - Chat interface component for AI Assistant
 * Integrates with existing ChatChannel infrastructure
 * Features dark theme matching the tax assistant reference
 */
const AIAssistantChat = ({
  currentUser,
  suggestedConversations = [],
  onStartConversation,
  selectedConversation = null,
  onConversationStarted,
  isLoading = false,
  isMinimized = false
}) => {
  const intl = useIntl();
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [conversationStarted, setConversationStarted] = useState(false);
  const [widgets, setWidgets] = useState([]);
  const messagesEndRef = useRef(null);
  const consumer = useRef(null);
  const subscription = useRef(null);
  const inputRef = useRef(null);

  // AI Assistant conversation ID - we'll create a special conversation type
  const aiConversationId = `ai_assistant_${currentUser?.id}`;

  // State for agent insights as suggestions
  const [agentInsights, setAgentInsights] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(false);

  // Check for new insights on component mount
  useEffect(() => {
    if (!currentUser?.id) return;

    const checkForNewInsights = async () => {
      setInsightsLoading(true);
      try {
        const response = await fetch('/ai_assistant/check_insights', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          }
        });

        const data = await response.json();
        
        if (data.success && data.insights && data.insights.length > 0) {
          // Transform insights into suggestion format
          const insightSuggestions = data.insights.map((insight, index) => ({
            id: `insight_${index}`,
            title: insight.title,
            description: insight.summary,
            icon: getInsightIcon(insight.agent_type),
            category: 'insight',
            priority: index,
            insightData: insight
          }));

          setAgentInsights(insightSuggestions);
        }
      } catch (error) {
        console.error('Failed to check for insights:', error);
      } finally {
        setInsightsLoading(false);
      }
    };

    checkForNewInsights();
  }, [currentUser?.id]);

  // Helper to get appropriate icon for insight type
  const getInsightIcon = (agentType) => {
    switch (agentType) {
      case 'EnterpriseInsightsAgent':
        return '⚠️';
      case 'ValuationAgent':
        return '💰';
      default:
        return '💡';
    }
  };

  // Initialize ActionCable connection for AI assistant
  useEffect(() => {
    if (!currentUser?.id) return;

    consumer.current = createConsumer();
    
    subscription.current = consumer.current.subscriptions.create(
      {
        channel: 'ChatChannel',
        room_id: aiConversationId
      },
      {
        connected: () => {
          console.log('Connected to AI Assistant chat');
        },
        
        disconnected: () => {
          console.log('Disconnected from AI Assistant chat');
        },
        
        received: (data) => {
          switch (data.type) {
            case 'message':
              const newMessage = {
                id: data.message.id || Date.now(),
                body: data.message.content || data.message.body,
                createdAt: data.message.sent || new Date().toISOString(),
                sender: {
                  id: data.message.sender?.id || 'ai_assistant',
                  name: data.message.sender?.name || 'Assistant',
                  allowed_name: data.message.sender?.allowed_name || 'Assistant',
                  isAI: data.message.sender?.id === 'ai_assistant' || data.message.sender?.isAI
                },
                read: data.message.read || true,
                messageType: data.message.messageType || data.message.message_type || 'message',
                insightTitle: data.message.insightTitle,
                insightDetails: data.message.insightDetails,
                suggestedActions: data.message.suggestedActions
              };
              
              setMessages(prev => {
                if (prev.some(m => m.id === newMessage.id)) {
                  return prev;
                }
                return [...prev, newMessage];
              });
              
              setIsTyping(false);
              break;
              
            case 'typing':
              if (data.user?.id === 'ai_assistant' && data.typing) {
                setIsTyping(true);
                setTimeout(() => setIsTyping(false), 3000);
              }
              break;
              
            default:
              break;
          }
        },
        
        speak: function(data) {
          return this.perform('speak', {
            room_id: aiConversationId,
            message: data.message
          });
        }
      }
    );

    return () => {
      if (subscription.current) {
        subscription.current.unsubscribe();
      }
      if (consumer.current) {
        consumer.current.disconnect();
      }
    };
  }, [currentUser?.id, aiConversationId]);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle selected conversation starter
  useEffect(() => {
    if (selectedConversation && selectedConversation.title && messages.length === 0) {
      // Automatically send the selected question
      const sendSelectedMessage = async () => {
        const messageText = selectedConversation.title;
        setConversationStarted(true);
        setIsSending(true);
        
        // Clear any existing widgets
        setWidgets([]);

        // Add user message immediately
        const userMessage = {
          id: Date.now(),
          body: messageText,
          createdAt: new Date().toISOString(),
          sender: {
            id: currentUser.id,
            name: currentUser.name,
            allowed_name: currentUser.name,
            isAI: false
          },
          read: true,
          messageType: 'message'
        };

        setMessages(prev => [...prev, userMessage]);

        try {
          // Send to AI assistant backend
          await sendToAIAssistant(messageText);
          // Notify parent that conversation started
          onConversationStarted?.();
        } catch (error) {
          console.error('Failed to send selected conversation:', error);
        } finally {
          setIsSending(false);
        }
      };

      sendSelectedMessage();
    }
  }, [selectedConversation, messages.length, currentUser, onConversationStarted]);

  // Handle sending message
  const handleSendMessage = async () => {
    if (!currentMessage.trim() || isSending) return;

    const messageText = currentMessage.trim();
    setCurrentMessage('');
    setIsSending(true);
    setConversationStarted(true);
    
    // Clear any existing widgets
    setWidgets([]);

    // Add user message immediately
    const userMessage = {
      id: Date.now(),
      body: messageText,
      createdAt: new Date().toISOString(),
      sender: {
        id: currentUser.id,
        name: currentUser.name,
        allowed_name: currentUser.name,
        isAI: false
      },
      read: true,
      messageType: 'message'
    };

    setMessages(prev => [...prev, userMessage]);

    try {
      // Send to AI assistant backend
      await sendToAIAssistant(messageText);
    } catch (error) {
      console.error('Failed to send message to AI:', error);
      // Add error message
      const errorMessage = {
        id: Date.now() + 1,
        body: intl.formatMessage({ id: 'succession.agent_system.ai_assistant_chat.error_processing' }),
        createdAt: new Date().toISOString(),
        sender: {
          id: 'ai_assistant',
          name: intl.formatMessage({ id: 'succession.agent_system.common.assistant' }),
          allowed_name: intl.formatMessage({ id: 'succession.agent_system.common.assistant' }),
          isAI: true
        },
        read: true,
        messageType: 'error'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsSending(false);
    }
  };

  // Send message to AI assistant backend
  const sendToAIAssistant = async (message) => {
    setIsTyping(true);
    
    try {
      // Send to our AI assistant backend
      const response = await fetch('/ai_assistant/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        body: JSON.stringify({
          message: message,
          conversation_id: aiConversationId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('AI Assistant response:', data);
      
      if (data.success) {
        // Check if there's an agent handoff
        const isValuationAgent = data.response.agent_handoff?.agent_type === 'valuation_chat' || 
                                 data.active_agent === 'valuation_chat';

        // Parse the message to extract widgets
        const parsedMessage = parseAgentMessage(data.response.message);
        
        // Extract widget if present
        const messageWidgets = parsedMessage.widget ? [parsedMessage.widget] : (data.widgets || []);

        const aiMessage = {
          id: Date.now() + 2,
          body: parsedMessage.cleanMessage,
          createdAt: new Date().toISOString(),
          sender: {
            id: isValuationAgent ? 'valuation_assistant' : 'ai_assistant',
            name: isValuationAgent ? 'Valuation Assistant' : 'Assistant',
            allowed_name: isValuationAgent ? 'Valuation Assistant' : 'Assistant',
            isAI: true
          },
          read: true,
          messageType: data.response.type || 'message',
          widgets: messageWidgets,
          suggestedActions: parsedMessage.suggestedActions || data.response.suggested_actions || [],
          agentHandoff: data.response.agent_handoff || null,
          dcfId: parsedMessage.dcfId
        };

        setMessages(prev => [...prev, aiMessage]);
        
        // Update widgets state if we have any
        if (messageWidgets.length > 0) {
          setWidgets(messageWidgets);
        }

        // If this is an agent handoff, add a system message to indicate the transition
        if (data.response.agent_handoff && data.response.agent_handoff.session_active) {
          const handoffMessage = {
            id: Date.now() + 3,
            body: `🤝 You're now chatting with the ${data.response.agent_handoff.agent_name}. All your future messages in this conversation will be handled by this specialized agent.`,
            createdAt: new Date().toISOString(),
            sender: {
              id: 'system',
              name: 'System',
              allowed_name: 'System',
              isAI: false,
              isSystem: true
            },
            read: true,
            messageType: 'handoff_notice'
          };

          setTimeout(() => {
            setMessages(prev => [...prev, handoffMessage]);
          }, 1000); // Slight delay to show the handoff message after the agent response
        }

        // Update widgets if provided
        if (data.widgets && data.widgets.length > 0) {
          setWidgets(data.widgets);
        }
      } else {
        console.error('AI response error:', data.error || 'No success flag');
        throw new Error(data.error || 'Failed to get AI response');
      }
    } catch (error) {
      console.error('AI Assistant error:', error);
      
      // Add error message
      const errorMessage = {
        id: Date.now() + 2,
        body: intl.formatMessage({ id: 'succession.agent_system.ai_assistant_chat.error_connecting' }),
        createdAt: new Date().toISOString(),
        sender: {
          id: 'ai_assistant',
          name: 'Assistant',
          allowed_name: 'Assistant',
          isAI: true
        },
        read: true,
        messageType: 'error'
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };


  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setCurrentMessage(suggestion.title);
    setConversationStarted(true);
    onStartConversation?.(suggestion);
    // Auto-send the suggestion
    setTimeout(() => {
      handleSendMessage();
    }, 100);
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle widget action clicks
  const handleWidgetAction = (action) => {
    console.log('Widget action:', action);
    
    // Handle URL actions first
    if (action.url) {
      console.log('Navigating to URL:', action.url);
      window.location.href = action.url;
      return;
    }
    
    // Handle action property for AI Assistant suggested actions
    if (action.action) {
      switch (action.action) {
        case 'open_valuation_chat':
          // Trigger the valuation chat window by dispatching a custom event
          const event = new CustomEvent('openValuationChat', {
            detail: { source: 'ai_assistant' }
          });
          window.dispatchEvent(event);
          break;
        default:
          console.log('Unknown action:', action.action);
      }
      return;
    }

    // Handle specific action types
    if (action.type) {
      switch (action.type) {
        case 'view_progress_details':
          window.location.href = '/succession/succession_planning';
          break;
        case 'detailed_valuation':
          // Navigate to valuation page instead of chat
          window.location.href = '/succession/valuation';
          break;
        case 'complete_checklist':
          // Navigate to the appropriate checklist page
          window.location.href = '/succession/bcp';
          break;
        case 'view_marketplace':
          window.location.href = '/succession/marketplace';
          break;
        case 'explore_opportunities':
          window.location.href = '/succession/marketplace';
          break;
        case 'benchmark_analysis':
          window.location.href = '/succession/valuation?section=benchmarks';
          break;
        case 'view_dashboard':
          window.location.href = '/succession/dashboard';
          break;
        case 'review_ebitda':
          window.location.href = '/succession/valuation?section=ebitda';
          break;
        case 'add_historical_data':
          window.location.href = '/succession/valuation?section=historical';
          break;
        case 'connect_quickbooks':
          window.location.href = '/succession/integrations/quickbooks';
          break;
        case 'professional_review':
          window.location.href = '/succession/professional_services';
          break;
        case 'add_data':
          if (action.field) {
            window.location.href = `/succession/valuation?field=${action.field}`;
          } else {
            window.location.href = '/succession/valuation';
          }
          break;
        case 'start_valuation_chat':
          // Trigger the valuation chat window by dispatching a custom event
          // This allows the ValuationAgentWidget to open its chat window
          const event = new CustomEvent('openValuationChat', {
            detail: { source: 'ai_assistant' }
          });
          window.dispatchEvent(event);
          break;
        // Widget-specific actions
        case 'continue_planning':
          window.location.href = '/succession/succession_planning';
          break;
        case 'view_valuation':
          window.location.href = '/succession/valuation';
          break;
        case 'get_detailed_report':
          window.location.href = '/succession/valuation';
          break;
        case 'review_factors':
          window.location.href = '/succession/valuation?section=factors';
          break;
        case 'start_bcp':
          window.location.href = '/succession/bcp';
          break;
        case 'complete_bcp':
          window.location.href = '/succession/bcp';
          break;
        case 'view_catastrophe_plan':
          window.location.href = '/succession/catastrophe';
          break;
        case 'complete_succession_plan':
          window.location.href = '/succession/succession_planning';
          break;
        default:
          console.log('Unknown action type:', action.type);
          // If the action has a widget property, check for common patterns
          if (action.widget) {
            const title = action.widget.title?.toLowerCase() || '';
            if (title.includes('valuation')) {
              window.location.href = '/succession/valuation';
            } else if (title.includes('bcp') || title.includes('continuity')) {
              window.location.href = '/succession/bcp';
            } else if (title.includes('succession')) {
              window.location.href = '/succession/succession_planning';
            } else if (title.includes('catastrophe')) {
              window.location.href = '/succession/catastrophe';
            }
          }
      }
    }
    
    // Handle simple label-based actions (for action_widget type)
    if (action.label) {
      console.log('Handling label action:', action.label);
      switch (action.label) {
        case 'Complete Succession Plan':
          window.location.href = '/succession/succession_planning';
          break;
        case 'Complete your Business Continuity Plan':
        case 'Complete Your Business Continuity Plan':
        case 'Start Your BCP':
        case 'Start your BCP':
          window.location.href = '/succession/bcp';
          break;
        case 'Find Successors':
          window.location.href = '/succession/search?view_type=map';
          break;
        case 'Schedule Consultation':
          window.location.href = '/succession/consultation';
          break;
        case 'View BCP Templates':
          window.location.href = '/succession/bcp/templates';
          break;
        case 'Get Practice Valuation':
          window.location.href = '/succession/valuation';
          break;
        case 'Search Map View':
          window.location.href = '/succession/search?view_type=map';
          break;
        case 'Search List View':
          window.location.href = '/succession/search?view_type=list';
          break;
        case 'View My Profile':
          window.location.href = '/succession/profile';
          break;
        default:
          console.log('Unknown action label:', action.label);
      }
    }
  };

  return (
    <div className="flex-1 flex flex-col">
      {/* Greeting or Conversation */}
      {!conversationStarted && messages.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-center p-3">
          <div>
            <div className="text-xl font-semibold text-succession-text-primary mb-6">{intl.formatMessage({ id: 'succession.agent_system.ai_assistant_chat.greeting' }, { name: currentUser?.name || 'there' })}</div>
            <div className="flex flex-wrap gap-2 items-center justify-center text-center">
              {suggestedConversations.slice(0, 6).map((suggestion) => (
                <button
                  key={suggestion.id}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="px-4 py-2 rounded-full text-xs bg-succession-bg-secondary text-succession-text-secondary hover:bg-succession-hover-bg border border-succession-border"
                >
                  {suggestion.title}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-auto p-3">
          <div className="space-y-4">
            {/* Messages in AssistantCard style */}
            {messages.map((message) => (
              <div key={message.id}>
                {/* User question bubble */}
                {!message.sender.isAI && (
                  <div className="max-w-[85%]">
                    <div className="text-sm text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'succession.agent_system.common.you' })}</div>
                    <div className="rounded-xl bg-succession-bg-secondary px-4 py-3 text-succession-text-primary">{message.body}</div>
                  </div>
                )}
                {/* Assistant answer bubble */}
                {message.sender.isAI && (
                  <div className="max-w-[95%]">
                    <div className="text-sm text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'succession.agent_system.common.assistant' })}</div>
                    <div className="rounded-xl border border-succession-border px-4 py-3 text-succession-text-primary bg-succession-bg-primary relative group">
                      <div className="chat_result" dangerouslySetInnerHTML={{ __html: message.body }} />
                      <CopyMessageButton
                        content={message.body}
                        isHtml={true}
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100"
                      />
                    </div>
                  </div>
                )}

                {/* Show widgets if any */}
                {message.sender.isAI && widgets && widgets.length > 0 && (
                  <div className="mt-4">
                    <AgentInfoWidgets
                      widgets={widgets}
                      onAction={handleWidgetAction}
                    />
                  </div>
                )}
              </div>
            ))}

            <div ref={messagesEndRef} />
          </div>
        </div>
      )}

      {/* Chips carousel at bottom */}
      <div className="border-t border-succession-border px-3 py-1">
        <div className="w-full overflow-x-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="flex gap-2 min-w-full pr-1" style={{ contain: 'layout paint size' }}>
            {/* Use existing PersonaChips but adapt for our suggestions */}
            <div className="flex flex-wrap gap-2 items-center justify-center text-center">
              {(agentInsights.length > 0 ? agentInsights : suggestedConversations).slice(0, 6).map((suggestion) => (
                <button
                  key={suggestion.id}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="px-4 py-2 rounded-full text-xs bg-succession-bg-secondary text-succession-text-secondary hover:bg-succession-hover-bg border border-succession-border"
                >
                  {suggestion.title}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Input row */}
      <div className="px-3 py-1 border-t border-succession-border flex items-center gap-2">
        <input
          ref={inputRef}
          value={currentMessage}
          onChange={(e) => setCurrentMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={intl.formatMessage({ id: 'succession.agent_system.ai_assistant_chat.ask_question_placeholder' })}
          className="flex-1 border border-succession-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-succession-primary bg-succession-bg-primary text-succession-text-primary placeholder-succession-text-secondary"
          disabled={isSending}
        />
        <button
          onClick={handleSendMessage}
          disabled={!currentMessage.trim() || isSending}
          className="inline-flex items-center px-4 py-2 rounded-md bg-succession-primary text-white hover:bg-succession-primary-hover"
        >
          {intl.formatMessage({ id: 'succession.agent_system.ai_assistant_chat.submit' })}
        </button>
      </div>
    </div>
  );
};

export default AIAssistantChat;